import React from 'react'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Column,
  Columns,
  IconClose,
  IconEyeSlash,
  IconWarningNew,
  Inline,
  List,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import ENV_VARS from '@/config'

const { DASHBOARD_URL } = ENV_VARS

interface Props {
  isListingVerified: boolean
  isLandlordVerified: boolean
  listingId: string
  closeWarningNotification: () => void
}

const ListingWarningNotification = ({
  isListingVerified,
  isLandlordVerified,
  listingId,
  closeWarningNotification
}: Props) => {
  const messageArr = []
  const { t } = useTranslation()

  if (!isListingVerified) {
    messageArr.push({
      message: t(
        'listing:b.listing.preview.warning.listing_unverified.message',
        'This listing is unverified. Verified listings receive 5X the engagements.'
      ),
      linkText: t(
        'listing:b.listing.preview.warning.verify_listing.message',
        'Verify your listing'
      ),
      url: `${DASHBOARD_URL}/listing/verify/${listingId}`
    })
  }

  if (!isLandlordVerified) {
    messageArr.push({
      message: t(
        'listing:b.listing.preview.warning.landlord_unverified.message',
        'Your ID is unverified. Verified landlords receive 3X the inquiries more than unverified landlords.'
      ),
      linkText: t(
        'listing:b.listing.preview.warning.verify_id.message',
        'Verify your ID'
      ),
      url: `${DASHBOARD_URL}/profile?openVerifyUserModal=true`
    })
  }

  const isMobile = useResponsiveValue()({
    desktop: false,
    tablet: false,
    mobile: true
  })

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box
        style={{ width: isMobile ? 'unset' : '100%', maxWidth: '1240px' }}
        padding="small"
        paddingY="medium"
        background="cautionLight"
        width="full"
        maxWidth="xlarge"
        borderRadius="large"
        boxShadow="borderCautionLight"
        marginX="small"
      >
        <Columns space="none" alignY="top">
          <Column width="4/5">
            <Columns space="xsmall" alignY="top">
              <Column width="content">
                <IconWarningNew size="xsmall" />
              </Column>
              <Column>
                <Stack space="gutter">
                  <Box paddingY="xxxxsmall">
                    <Text weight="strong" size="small">
                      {t(
                        'listing:b.listing.preview.warning.notifications.number',
                        {
                          defaultValue: 'You have {{count}} notification(s): ',
                          count: messageArr.length
                        }
                      )}
                    </Text>
                  </Box>
                  <List>
                    {messageArr.map(item => (
                      <Text key={item.message} size="small">
                        {item.message}{' '}
                        <Box
                          component={TextLink}
                          href={item.url}
                          target="_blank"
                          // using vanilla extract is better
                          style={{ color: '#3148C9' }}
                        >
                          {item.linkText}
                        </Box>
                      </Text>
                    ))}
                  </List>
                  <Columns
                    space="xxsmall"
                    alignY={{ mobile: 'top', desktop: 'center' }}
                  >
                    <Column width="content">
                      <IconEyeSlash tone="caution" />
                    </Column>
                    <Column>
                      <Text size="small">
                        <i>
                          {t(
                            'listing:b.listing.preview.warning.annotation.message',
                            'This alert is only visible to you and any managing landlords.'
                          )}
                        </i>
                      </Text>
                    </Column>
                  </Columns>
                </Stack>
              </Column>
            </Columns>
          </Column>
          <Column>
            <Inline align="right" space="none">
              <Box
                onClick={closeWarningNotification}
                style={{ cursor: 'pointer' }}
              >
                <IconClose tone="secondary" size="xsmall" />
              </Box>
            </Inline>
          </Column>
        </Columns>
      </Box>
    </Box>
  )
}

export default ListingWarningNotification
